import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { combineStrings } from 'assets/utils/data/String';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import { keys } from 'lodash';
import User from 'models/core/user/Model.User';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import Product from 'models/productManagement/product/Model.Product';
import VehicleModel from 'models/productManagement/vehicleModel/Model.VehicleModel';
import Contract from 'models/sales/contract/Model.Contract';
import contractStatusFlowApi from 'models/sales/contract/statusFlow/Api.Contract.StatusFlow';
import Distributor from 'models/sales/distributor/Model.Distributor';
import { useMemo } from 'react';
import { contractIndexRoute, otherDealerContractIndexRoute } from './Contract.Index';

export default function useContract(isOtherDealerContracts = false) {
  const { lang } = getLocales();
  const { permissions, user, isDealer, isDistributor } = useAuthStore();
  const pageRouter = usePageRouter<Module.Claims.Contract.Params, Module.Claims.Contract.Query>({
    route: isOtherDealerContracts ? otherDealerContractIndexRoute : contractIndexRoute,
  });

  const canSearchContractsUnrestricted = isOtherDealerContracts && !!permissions.SEARCH_CONTRACT_UNRESTRICTED;
  const hasAccess = isOtherDealerContracts
    ? canSearchContractsUnrestricted
    : permissions.VIEW_OWNED_CONTRACTS_IN_CLAIMS;
  const hasRequiredUnrestrictedFilter = !!pageRouter.query.list?.contractNumber || !!pageRouter.query.list?.vin;
  const isMissingRequiredUnrestrictedFilter = canSearchContractsUnrestricted && !hasRequiredUnrestrictedFilter;

  const isClaimAdmin = permissions?.CLAIM_ADMIN;
  const canCreateClaim = isClaimAdmin || permissions?.CREATE_NEW_CLAIM;
  const canAddClaim = canCreateClaim;

  const listApi = useApi(
    {
      action: canSearchContractsUnrestricted ? Contract.listUnrestricted : Contract.list,
      default: {
        limit: defaultLimit,
        offset: defaultOffset,
        dealerId: canSearchContractsUnrestricted ? [] : (user?.dealers?.map((d) => d.id) ?? []),
        distributorId: canSearchContractsUnrestricted ? undefined : (user?.distributorId ?? undefined),
        includeClaims: true,
      },
      body: {
        ...pageRouter.query.list,
      },
      callback: (req) => {
        pageRouter.updateQuery({ ...pageRouter.query, list: req });
      },
      wait: !hasAccess || isMissingRequiredUnrestrictedFilter,
    },
    [JSON.stringify(pageRouter.query.list)]
  );

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const dealersApi = useApi({
    action: Dealer.list,
    wait: true,
    default: {
      id: canSearchContractsUnrestricted ? [] : (user?.dealers?.map((d) => d.id) ?? []),
      distributorId: canSearchContractsUnrestricted
        ? undefined
        : user?.distributorId
          ? [user?.distributorId]
          : undefined,
      isInactive: false,
    },
  });
  const distributorsApi = useApi({
    action: Distributor.list,
    wait: !isDealer && !isDistributor,
  });

  const userListApi = useApi({
    action: User.list,
    default: {
      isDisabled: false,
      dealerId: canSearchContractsUnrestricted ? [] : (user?.dealers?.map((d) => d.id) ?? []),
      distributorId: canSearchContractsUnrestricted
        ? undefined
        : user?.distributorId
          ? [user?.distributorId]
          : undefined,
    },
    wait: !hasAccess || !permissions.CREATE_CONTRACT,
  });

  const productsApi = useApi({
    action: Product.list,
    wait: true,
    default: {
      dealerId: canSearchContractsUnrestricted ? [] : (user?.dealers?.map((d) => d.id) ?? []),
      distributorId: canSearchContractsUnrestricted
        ? undefined
        : user?.distributorId
          ? [user?.distributorId]
          : undefined,
    },
  });

  const modelsApi = useApi({
    action: VehicleModel.list,
    wait: true,
  });

  const contractStatusInfoApi = useApi({ action: contractStatusFlowApi });
  const contractStatusInfo = contractStatusInfoApi.payload;

  const statuses = useMemo(() => {
    return keys(contractStatusInfo).map((status) => {
      const [primary, secondary, reason] = (status ? lang[status] : status).split('_');
      const mainStatus = combineStrings(' - ', primary, secondary);
      return {
        id: status,
        subtitle: mainStatus,
        title: reason ?? mainStatus,
      };
    });
  }, [contractStatusInfo]);

  return {
    hasAccess,
    isOtherDealerContracts,
    canSearchContractsUnrestricted,
    isMissingRequiredUnrestrictedFilter,
    pageRouter,
    listApi,
    userListApi,
    selected,
    dealersApi,
    distributorsApi,
    modelsApi,
    productsApi,
    permissions,
    statuses,
    isDealer,
    isDistributor,
    canAddClaim,
  };
}

export function useContractProvider() {
  return useDataProvider<ReturnType<typeof useContract>>();
}
